import {memo, useEffect} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Controller, useFormContext} from 'react-hook-form';
import {EXTRA_FORM_NAMES} from '../constants';
import type {AddFormType, FormTypes} from '../types';
import {Contract, getRegExpFromString, GUEST_FORM_NAMES} from '@chekin/core';
import {cn, Checkbox, Skeleton} from '@chekinapp/ui';
import {useErrorModal} from '../../../hooks/useErrorModal';
import {useGuestFormEnv} from '../env';
import {useGuestSchema} from '../GuestSchemaContext';
import {downloadFromLink} from '../helpers';
import {PrivacyPolicyLink} from './PrivacyPolicyLink';
import {GuestEmailField} from '../ui/GuestEmailField';
import {FieldsGroup} from '../ui/FieldsGroup';
import {ComputedBox} from '../ui/ComputedBox';

const checkHasAlreadyEmail = (formFields: AddFormType) =>
  (formFields as AddFormType)?.[GUEST_FORM_NAMES.email]?.is_active &&
  (formFields as AddFormType)?.[GUEST_FORM_NAMES.email]?.is_required;

type ContractEmailCheckboxProps = {
  isLoading?: boolean;
  defaultEmail?: string;
};

const ContractEmailCheckbox = memo(
  ({isLoading, defaultEmail}: ContractEmailCheckboxProps) => {
    const {t} = useTranslation();
    const {isContractEnabled} = useGuestFormEnv();
    const {control, watch, resetField, getValues} = useFormContext<FormTypes>();
    const {formFields} = useGuestSchema();
    const hasAlreadyEmail = checkHasAlreadyEmail(formFields);

    const shouldSendCopyContractToEmail = watch(
      EXTRA_FORM_NAMES.send_copy_contract_email,
    );
    const needToShowAdditionalEmailField =
      !defaultEmail && !hasAlreadyEmail && shouldSendCopyContractToEmail;

    useEffect(
      function resetInactiveContractEmailField() {
        const value = getValues()[GUEST_FORM_NAMES.contract_email];
        if (!needToShowAdditionalEmailField && value) {
          resetField(GUEST_FORM_NAMES.contract_email);
        }
      },
      [getValues, needToShowAdditionalEmailField, resetField],
    );

    if (!isContractEnabled) return null;

    if (isLoading) {
      return (
        <div
          role="status"
          aria-label={t('loading')}
          className="sign-block__checkbox-container flex w-full items-start gap-3"
        >
          <Skeleton className="size-6 shrink-0 rounded-[6px]" />
          <div className="flex min-w-0 flex-1 flex-col gap-2 pt-0.5">
            <Skeleton className="h-[13px] w-[74%] rounded" />
            <Skeleton className="h-[13px] w-[42%] rounded" />
          </div>
        </div>
      );
    }

    return (
      <div className={cn('sign-block__checkbox-container', 'w-full')}>
        <Controller
          name={EXTRA_FORM_NAMES.send_copy_contract_email}
          control={control}
          render={({field, fieldState: {error}}) => (
            <Checkbox
              label={<Trans i18nKey="send_copy_contract_email" />}
              error={error?.message}
              className="sign-block__checkbox items-start"
              labelClassName="!text-[14px] !font-normal"
              {...field}
            />
          )}
        />
        <ComputedBox
          expanded={Boolean(needToShowAdditionalEmailField)}
          className="max-w-[293px] pt-4"
        >
          <GuestEmailField
            name={GUEST_FORM_NAMES.contract_email}
            required={t('required')}
            label={t('email')}
            format={getRegExpFromString(
              formFields?.[GUEST_FORM_NAMES.email]?.format || '',
            )}
          />
        </ComputedBox>
      </div>
    );
  },
);
ContractEmailCheckbox.displayName = 'ContractEmailCheckbox';

export type LegalTextKeys = {
  submit: string;
  signNow: string;
};

const DEFAULT_LEGAL_TEXT_KEYS: LegalTextKeys = {
  submit: 'by_clicking_next_you_accept_the',
  signNow: 'by_clicking_signt_now_you_accept_the',
};

type ContractDisclosureProps = {
  contracts?: Contract[];
  hasError?: boolean;
  isAutomatedSignature?: boolean;
  legalTextKeys?: LegalTextKeys;
  isProminent?: boolean;
  onOpenDocument?: (url: string) => void;
};

const ContractDisclosure = memo(
  ({
    contracts,
    hasError,
    isAutomatedSignature,
    legalTextKeys = DEFAULT_LEGAL_TEXT_KEYS,
    isProminent,
    onOpenDocument,
  }: ContractDisclosureProps) => {
    const {t} = useTranslation();
    const {ErrorModal: ErrorModalContract, displayError} = useErrorModal();
    const {isContractEnabled} = useGuestFormEnv();

    const viewContract = () => {
      const contract = contracts?.[0];

      if (hasError || !contract?.file) {
        displayError();
        return;
      }

      if (onOpenDocument) {
        onOpenDocument(contract.file);
        return;
      }

      downloadFromLink(contract.file);
    };

    return (
      <>
        <div
          className={cn(
            'sign-block__warning text-left [&_a]:font-bold [&_a]:[font-size:inherit] [&_a]:[line-height:inherit] [&_a]:text-[var(--chekin-color-brand-blue)] [&_b]:font-bold',
            isProminent
              ? 'text-[14px] font-medium leading-[21px] text-[var(--chekin-text-secondary)]'
              : 'text-[12.5px] font-normal leading-[1.5] text-[var(--chekin-color-gray-2)]',
          )}
        >
          {!isContractEnabled && !isAutomatedSignature && (
            <Trans i18nKey={legalTextKeys.submit}>
              By clicking <b>‘Submit’</b> you accept to sign the form with your name and
              the Privacy Policy
            </Trans>
          )}
          {!isContractEnabled && isAutomatedSignature && (
            <Trans i18nKey={legalTextKeys.signNow}>
              By clicking <b>‘Sign now’ </b>you accept to sign the form with your name,
              the short term
            </Trans>
          )}
          {isContractEnabled && (
            <>
              {!isAutomatedSignature ? (
                <Trans i18nKey={legalTextKeys.submit}>
                  By clicking <b>‘Submit’</b> you accept to sign the form with your name
                  and the
                </Trans>
              ) : (
                <Trans i18nKey={legalTextKeys.signNow}>
                  By clicking <b>‘Sign now’ </b>you accept to sign the form with your
                  name, the short term
                </Trans>
              )}{' '}
              <span
                data-testid="contract-btn"
                onClick={viewContract}
                className="cursor-pointer font-bold text-[var(--chekin-color-brand-blue)]"
              >
                {t('contract')}
              </span>
            </>
          )}
          {isContractEnabled && ` ${t('and_you_accept_our')} `}
          <PrivacyPolicyLink onOpenDocument={onOpenDocument} />
        </div>
        <ErrorModalContract
          title={t('contract_not_available')}
          message={t('contract_not_available_description')}
        />
      </>
    );
  },
);
ContractDisclosure.displayName = 'ContractDisclosure';

type ContractBoxProps = ContractEmailCheckboxProps & ContractDisclosureProps;

const ContractBox = memo((props: ContractBoxProps) => {
  const {t} = useTranslation();
  const {isContractEnabled} = useGuestFormEnv();

  return (
    <>
      {isContractEnabled && !props.isLoading && (
        <FieldsGroup title={t('contract')} centered={false}>
          <ContractEmailCheckbox
            isLoading={props.isLoading}
            defaultEmail={props.defaultEmail}
          />
        </FieldsGroup>
      )}
      <ContractDisclosure
        contracts={props.contracts}
        hasError={props.hasError}
        isAutomatedSignature={props.isAutomatedSignature}
        onOpenDocument={props.onOpenDocument}
      />
    </>
  );
});
ContractBox.displayName = 'ContractBox';

export {ContractBox, ContractEmailCheckbox, ContractDisclosure};
